/* 
As a user, I want to see a list of projects that are available through Love Big,
so that I can find opportunities to reach out and help Love Big's partners.
When I click on the Explore page
Then I will be presented with a map of some featured projects and a list of the projects
as cards with basic information.
When I click on "View Project", then I will be presented with the page for that specific project
with more details.
When I select a parameter such as Location and Type and click Search
Then I can see projects that fit my needs and location specifically
*/

//Packages
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Geocode from "react-geocode";
//Components
import ProjectCard from "./ProjectCard";
import MapContainer from "../../components/MapContainer";
//Material UI Components
import {
  Grid,
  Select,
  Button,
  MenuItem,
  CircularProgress,
  Typography,
} from "@mui/material";
//Animation
import { motion } from "framer-motion";
//API Route
import { Route } from "../../utils/config";
//Assets
import placeholderImg from "../../assets/images/placeholderLg.png";
//Styles
import "./styles.css";

Geocode.setApiKey("AIzaSyAAAj1P59p4tswF_Xdv7-aMr9ybbpr1aWg");
Geocode.setLanguage("en");

export default function Explore() {
  //Project Data stored in state
  const [projects, setProjects] = useState([]);

  //Data for dropdown menus
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [center, setCenter] = useState({
    lat: 7.08,
    lng: 21.48,
  });
  const [zoomLevel, setZoomLevel] = useState(2);

  //Selected search parameters that will be used to query projects
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedContinent, setSelectedContinent] = useState("");

  useEffect(() => {
    if (selectedLocation) {
      Geocode.fromAddress(selectedLocation).then(
        (response) => {
          const { lat, lng } = response.results[0].geometry.location;
          setCenter({ lat, lng });
          setZoomLevel(7);
        },
        (error) => {
          console.error(error);
        }
      );
    }
  }, [selectedLocation]);

  //Array of map locations to display once the data is loaded
  const [mapLocations, setMapLocations] = useState([]);

  //Boolean to check if list is finished. If listFinished then the user cannot load
  //more data and the page will reflect that under the list of projects.
  const [listFinished, setListFinished] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const pageRef = useRef(1);
  const handleClickProject = (id) => {
    let customMapLocations = JSON.parse(JSON.stringify(mapLocations));
    let clickedLocation = customMapLocations.find(
      (location) => location.id === id
    );
    clickedLocation.openPopup = true;
    setMapLocations(customMapLocations);
  };

  const handleMouseLeaveProject = (id) => {
    let customMapLocations = JSON.parse(JSON.stringify(mapLocations));
    let clickedLocation = customMapLocations.find(
      (location) => location.id === id
    );
    clickedLocation.openPopup = false;
    setMapLocations(customMapLocations);
  };

  const clearFilter = () => {
    setSelectedProject("");
    setSelectedLocation("");
    setSelectedContinent("");
    searchProjects(true);
  };

  function getData() {
    pageRef.current++;
    axios
      .post(`${Route}/Projects/GetProjectsToExplore`, {
        pageNumber: 1,
        pageSize: 4,
        projectLocation: "",
        projectType: "",
      })
      .then((res) => {
        setLocations(res.data.locations);
        setMapLocations(
          res.data.projects.map((project) => {
            return {
              lat: project.latitude,
              lng: project.longitude,
              id: project.id,
              name: project.name,
              overview: project.overview,
              openPopup: true,
            };
          })
        );

        setProjects(res.data.projects);
        setIsLoading(false);
      })
      .catch((err) => {
        throw err;
      });
  }

  const getDropdownValues = () => {
    axios
      .get(`${Route}/Application/GetSystemData`)
      .then((res) => {
        setTypes(res.data.specialties);
      })
      .catch((err) => {
        throw err;
      });
  };

  useEffect(() => {
    getData();
    getDropdownValues();
    window.scrollTo(0, 0);
  }, []);

  const searchProjects = (isClear = false) => {
    setIsLoading(true);
    axios
      .post(`${Route}/Projects/GetProjectsToExplore`, {
        pageNumber: 1,
        pageSize: 4,
        projectLocation: isClear ? "" : selectedLocation,
        projectType: isClear ? "" : selectedProject,
      })
      .then((res) => {
        pageRef.current++;
        if (res.data.projects.length > 0) {
          setProjects(res.data.projects);
          setMapLocations(
            res.data.projects.map((project) => {
              return {
                lat: project.latitude,
                lng: project.longitude,
                id: project.id,
                name: project.name,
                overview: project.overview,
                openPopup: true,
              };
            })
          );
          setListFinished(false);
        } else {
          setProjects([]);
          setListFinished(true);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        throw err;
      });
  };

  const seeMore = () => {
    setIsLoading(true);

    axios
      .post(`${Route}/Projects/GetProjectsToExplore`, {
        pageNumber: pageRef.current,
        pageSize: 4,
        projectLocation: selectedLocation,
        projectType: selectedProject,
      })
      .then((res) => {
        pageRef.current++;
        if (res.data.projects.length > 0) {
          setProjects(projects.concat(res.data.projects));

          setMapLocations(
            projects.concat(res.data.projects).map((project) => {
              return { 
                lat: project.latitude, 
                lng: project.longitude,
                id: project.id,
                name: project.name,
                overview: project.overview,
                openPopup: true,
              };
            })
          );
        } else {
          pageRef.current = 0;
          setListFinished(true);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        throw err;
      });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="explore-header">
        <div className="explore-header-title">
          <Typography variant="h4" color="#0060a4" mb={4}>
            Explore
          </Typography>
        </div>
        <Typography
          variant="h5"
          color="black"
          className="explore-header-desktop"
        >
          Love Big connects and coalesces great
          <br />
          hearts and minds to create synergy
          <br />
          around resolving the greatest needs.
        </Typography>
        <Typography
          variant="h5"
          color="black"
          className="explore-header-mobile"
        >
          Love Big connects and
          <br />
          coalesces great hearts and
          <br />
          minds to create synergy around
          <br />
          resolving the greatest needs.
        </Typography>
      </div>
      <div className="explore-page">
        <div className="explore-panel">
          <div className="explore-page-right-mobile">
            <Grid item xs={12}>
              <div className="explore-map-title">Explore Projects</div>
            </Grid>
            <Grid
              className="desktop-map"
              item
              xs={12}
              height={{ xs: "350px", md: "750px" }}
            >
              {!isLoading ? (
                <MapContainer locations={mapLocations}></MapContainer>
              ) : (
                <></>
              )}
            </Grid>
          </div>
          <div className="explore-page-left-content">
            <div className="filter-panel">
              <Select
                value={selectedContinent}
                sx={{ borderRadius: 100 }}
                displayEmpty
                onChange={(e) => setSelectedContinent(e.target.value)}
                inputProps={{ "aria-label": "Without label" }}
              >
                <MenuItem value="">Continent</MenuItem>
                <MenuItem value="africa">Africa</MenuItem>
                <MenuItem value="asia">Asia</MenuItem>
                <MenuItem value="oceana">Oceana</MenuItem>
                <MenuItem value="south-america">South America</MenuItem>
                <MenuItem value="north-america">North America</MenuItem>
              </Select>

              <Select
                value={selectedLocation}
                sx={{ borderRadius: 100 }}
                onChange={(e) => setSelectedLocation(e.target.value)}
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
              >
                <MenuItem value="">
                  <i className="fas fa-location-arrow"></i> Location
                </MenuItem>

                {locations.map((location) => {
                  return (
                    <MenuItem value={location} key={location}>
                      {location}
                    </MenuItem>
                  );
                })}
              </Select>

              <Select
                value={selectedProject}
                sx={{ borderRadius: 100 }}
                onChange={(e) => setSelectedProject(e.target.value)}
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
              >
                <MenuItem value="" disabled>
                  Project Type
                </MenuItem>
                <MenuItem value="" key="all">
                  All
                </MenuItem>
                {types.map((type) => {
                  return (
                    <MenuItem value={type.name} key={type.id}>
                      {type.name}
                    </MenuItem>
                  );
                })}
              </Select>
              <Button
                color="primary"
                variant="contained"
                size="medium"
                onClick={() => {
                  searchProjects();
                }}
              >
                Search Projects
              </Button>
              <Button
                color="secondary"
                size="small"
                onClick={() => {
                  clearFilter();
                }}
              >
                All
              </Button>
            </div>
            <Grid
              container
              item
              className="available-panel"
              xs={12}
              mt={3}
              justifyContent={{ xs: "space-evenly", md: "space-evenly" }}
              alignItems={{ xs: "center", md: "unset" }}
            >
              <Grid item width={{ xs: "90%", md: "75%" }} mt={3}>
                <div className="available-projects">
                  <h1>Available Projects</h1>
                </div>
              </Grid>
              <Grid container item justifyContent="center">
                {!isLoading ? (
                  <>
                    <Grid
                      container
                      item
                      columnSpacing={6}
                      spacing={4}
                      mb={2}
                      px={{ xs: 2, md: 4 }}
                    >
                      {projects.map((project) => {
                        return (
                          <ProjectCard
                            key={project.id}
                            id={project.id}
                            placeholderImg={placeholderImg}
                            overview={project.overview}
                            name={project.name}
                            type={project.projectType}
                            canClaim={project.projectStatus === 'Unclaimed' && project.serviceProviderName === ''}
                            handleClickProject={handleClickProject}
                            handleMouseLeaveProject={handleMouseLeaveProject}
                          ></ProjectCard>
                        );
                      })}
                    </Grid>
                    {listFinished ? (
                      <div className="no-projects">
                        No more projects to display.
                      </div>
                    ) : (
                      <div
                        onClick={() => seeMore()}
                        className="more-projects-btn"
                      >
                        See more
                      </div>
                    )}
                  </>
                ) : (
                  <Grid container justifyContent="center" mt={2} mb={2}>
                    <Grid item>
                      <CircularProgress></CircularProgress>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </div>
          <div className="explore-page-right-desktop">
            <Grid item xs={12}>
              <div className="explore-map-title">Explore Projects</div>
            </Grid>
            <Grid
              className="desktop-map"
              item
              xs={12}
              height={{ xs: "350px", md: "750px" }}
            >
              {!isLoading ? (
                <MapContainer
                  locations={mapLocations}
                  center={center || { lat: 7.08, lng: 21.48 }}
                  region="af"
                  zoomLevel={zoomLevel}
                ></MapContainer>
              ) : (
                <></>
              )}
            </Grid>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
